import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Trash2, Loader2, Bot, User, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function ChatbotWidget({ restaurantId = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Generate or retrieve session ID
  useEffect(() => {
    const storedSession = localStorage.getItem('chatbot_session');
    if (storedSession) {
      setSessionId(storedSession);
    } else {
      const newSession = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chatbot_session', newSession);
      setSessionId(newSession);
    }
  }, []);

  // Load history when opening
  useEffect(() => {
    if (isOpen && sessionId && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen, sessionId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const loadHistory = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/api/chatbot/history/${sessionId}`);
      if (res.data.messages?.length > 0) {
        setMessages(res.data.messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.created_at
        })));
      } else {
        // Add welcome message
        setMessages([{
          role: 'assistant',
          content: restaurantId 
            ? "Bonjour ! Je suis l'assistant LEMO. Je connais le menu et les horaires de ce restaurant. Comment puis-je vous aider ?"
            : "Bonjour ! Je suis l'assistant LEMO. Je peux vous aider avec vos questions sur nos restaurants, vous recommander des plats ou vous guider dans votre commande. Que puis-je faire pour vous ?",
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
    }
  };

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/chatbot/chat`, {
        message: userMessage.content,
        session_id: sessionId,
        restaurant_id: restaurantId
      });

      const assistantMessage = {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Update session ID if changed
      if (res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        localStorage.setItem('chatbot_session', res.data.session_id);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolé, une erreur s'est produite. Veuillez réessayer.",
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, restaurantId]);

  const clearHistory = async () => {
    if (!sessionId) return;
    try {
      await axios.delete(`${API}/api/chatbot/history/${sessionId}`);
      setMessages([{
        role: 'assistant',
        content: "Conversation réinitialisée. Comment puis-je vous aider ?",
        timestamp: new Date().toISOString()
      }]);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: "Horaires", message: "Quels sont vos horaires d'ouverture ?" },
    { label: "Menu", message: "Pouvez-vous me montrer le menu ?" },
    { label: "Recommandation", message: "Que me recommandez-vous ?" }
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        data-testid="chatbot-toggle"
        aria-label="Ouvrir le chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[500px] max-h-[80vh]'
      }`}
      data-testid="chatbot-widget"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-emerald-500 text-white rounded-t-2xl cursor-pointer"
        onClick={() => isMinimized && setIsMinimized(false)}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">LEMO Assistant</h3>
            {!isMinimized && (
              <p className="text-xs text-emerald-100">En ligne</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!isMinimized && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); clearHistory(); }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="Nouvelle conversation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="Minimiser"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div className={`px-3 py-2 rounded-2xl text-sm ${
                    msg.role === 'user' 
                      ? 'bg-emerald-500 text-white rounded-tr-sm' 
                      : msg.error 
                        ? 'bg-red-50 text-red-700 border border-red-200 rounded-tl-sm'
                        : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="px-4 py-3 bg-gray-100 rounded-2xl rounded-tl-sm">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick actions (only show at start) */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInput(action.message);
                    setTimeout(() => sendMessage(), 100);
                  }}
                  className="px-3 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded-full hover:bg-emerald-100 transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Écrivez votre message..."
                className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                disabled={loading}
                data-testid="chatbot-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                size="icon"
                className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ChatbotWidget;
